import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  Signal,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { UntilDestroy } from '@ngneat/until-destroy';

import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { LocalStorageService, StorageKey } from '@blaze-shared/platform-data-access';
import { DialogService } from '@blaze-shared/shared-feature';
import { ButtonComponent, TooltipDirective } from '@blaze-shared/shared-ui';

import { ReferralDialogComponent } from './referral-dialog/referral-dialog.component';

const TIME_BETWEEN_BANNER_APPEARANCE_DAYS = 30;

@UntilDestroy()
@Component({
  selector: 'blaze-referral',
  imports: [ButtonComponent, MatIconModule, NgTemplateOutlet, TooltipDirective],
  templateUrl: './referral.component.html',
  styleUrls: ['./referral.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralComponent {
  private readonly dialog = inject(MatDialog);
  private readonly dialogService = inject(DialogService);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly projectContextFacade = inject(ProjectContextFacade);

  type = input.required<'banner' | 'button'>();

  private readonly timeClosed = signal<number | undefined>(undefined);

  protected readonly shouldShowBanner: Signal<boolean>;
  protected readonly isReferralEnabled: Signal<boolean>;

  constructor() {
    this.shouldShowBanner = computed(() => this.closeTimeExceededTreshhold());
    this.isReferralEnabled = computed(
      () => !!this.projectContextFacade.account()?.showReferralInfo,
    );
  }

  protected onOpenReferralDialog(): void {
    this.dialogService
      .decorateDialogOpen(
        this.dialog.open(ReferralDialogComponent, {
          data: {},
          disableClose: false,
          hasBackdrop: true,
          panelClass: ['bd', 'bd-dialog'],
          height: 'auto',
          width: '550px',
          maxWidth: '550px',
        }),
      )
      .afterClosed();
  }

  /**
   * Keeps track of when the banner was closed in localStorage.
   */
  protected onCloseBanner(): void {
    const timestamp = Date.now();
    this.timeClosed.set(timestamp);
    this.localStorageService.set(StorageKey.ReferralBannerLastShown, timestamp);
  }

  /**
   * @returns true if provided close time exceeds TIME_BETWEEN_BANNER_APPEARANCE_DAYS.
   */
  private closeTimeExceededTreshhold(): boolean {
    const timeClosed =
      this.timeClosed() ?? this.localStorageService.get(StorageKey.ReferralBannerLastShown);

    if (!timeClosed) {
      return true;
    }

    const lastShownDateObj = new Date(timeClosed);
    const timeDiff = Date.now() - lastShownDateObj.getTime();
    const daysDiff = timeDiff / (1000 * 60 * 60 * 24);

    return daysDiff >= TIME_BETWEEN_BANNER_APPEARANCE_DAYS;
  }
}
